import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AutoSyncProvider } from "@/contexts/AutoSyncContext";
import Layout from "@/components/Layout";
import OfflineIndicator from "@/components/OfflineIndicator";
import "@/i18n";
import "@/App.css";

// Lazy load all pages for better performance
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const RegisterCompany = lazy(() => import("@/pages/RegisterCompany"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Leads = lazy(() => import("@/pages/Leads"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Team = lazy(() => import("@/pages/Team"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Reports = lazy(() => import("@/pages/Reports"));
const Products = lazy(() => import("@/pages/Products"));
const Messages = lazy(() => import("@/pages/Messages"));
const Conversations = lazy(() => import("@/pages/Conversations"));
const Settings = lazy(() => import("@/pages/Settings"));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#fdf6f0] to-[#fbe4d8]">
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto mb-4">
        <div className="absolute inset-0 rounded-full border-4 border-[#190019]/10"></div>
        <div className="absolute inset-0 rounded-full border-4 border-t-[#6b2d6b] animate-spin"></div>
      </div>
      <p className="text-[#4a1a4a] text-sm">جاري التحميل...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppContent() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-company" element={<RegisterCompany />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="team" element={<Team />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="reports" element={<Reports />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="products" element={<Products />} />
            <Route path="messages" element={<Messages />} />
            <Route path="conversations" element={<Conversations />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <AutoSyncProvider>
            <OfflineIndicator />
            <AppContent />
            <Toaster position="top-center" />
          </AutoSyncProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
